import * as React from "react";
import { useInstanceState } from "../instance/instance-store";
import { DEFAULT_TITLE, useTitleContext } from "../../contexts/TitleContext";
import { useRouteType } from "../../hooks/useRouteType";
import { QuickAgentChatPanel } from "./QuickAgentChatPanel";

/**
 * Wrapper around QuickAgentChatPanel that manages visibility and constructs
 * a one-time initialPrompt with page and instance context for the chat agent.
 *
 * Visibility rules:
 * - Search pages: sidebar auto-collapsed (chat rendered inline in SearchHome)
 * - Admin pages: panel completely hidden
 * - Other pages: collapsed sidebar tab
 *
 * On cold start (direct page open, not in-app navigation), waits for the page
 * title to be set then constructs a one-time initialPrompt with page and instance
 * context so the agent can greet the user with relevant context.
 */
const PROMPT_PREFIX = "This is silent context to be passed to the chat agent, the user is currently";
const PROMPT_SUFFIX = "Introduce yourself to the user by using their first name and tell them your name and what your capabilities are and ask if they need any help navigating PathFinder with you.";

export const QuickAgentChatPanelWrapper: React.FC<{ agentUrl: string }> = ({ agentUrl }) => {
    const { isAdmin, isSearch } = useRouteType();
    const { instanceId } = useInstanceState();
    const { title } = useTitleContext();

    // Build the initial prompt once — only on the first render where both
    // instanceId and a meaningful title (or non-page route) are available.
    const initialQuery = React.useMemo(() => {
        if (!instanceId) return undefined;

        const hasTitle = title && title !== DEFAULT_TITLE;
        if (hasTitle) {
            return `${PROMPT_PREFIX} on the page "${title}" on the ${instanceId} instance. ${PROMPT_SUFFIX}`;
        }

        // On non-page routes the title is never customized, use instance-only context
        const isPageRoute = /^#\/page\//.test(window.location.hash);
        if (!isPageRoute) {
            return `${PROMPT_PREFIX} on the ${instanceId} instance. ${PROMPT_SUFFIX}`;
        }

        // On page routes, wait for title to be set by the page component
        return undefined;
    }, [instanceId, title]);

    // Hide completely on admin pages
    if (isAdmin) {
        return null;
    }

    // Wait for initial prompt to be ready before mounting the iframe
    if (!initialQuery) {
        return null;
    }

    return (
        <QuickAgentChatPanel
            agentUrl={agentUrl}
            initialQuery={initialQuery}
            defaultCollapsed={true}
            autoCollapse={isSearch}
        />
    );
};
